import React from 'react';
import styled, { css } from 'styled-components';
import { Button } from '@lt/components';
import { SuccessMarkDone } from '@lt/components/icons-react/userflow';
import AgreeBlock from '../Agreement/AgreeBlock';
import { formatCurrency } from '../../../../../utils/stringUtils';
import { NBSP } from '../../../../../constants/common';
import {
  DiscountSuccessContentAnimation,
  DiscountSuccessIconAnimation,
  DiscountSuccessTitleAnimation,
} from '../animation';

interface GiftCertificateInfoProps {
  priceWithDiscount?: number;
  discount: number;
  onSubmit: () => void;
  onCancel: () => void;
  isMobile?: boolean;
}

const GiftCertificateInfo = ({
  priceWithDiscount = 0,
  discount,
  onSubmit,
  onCancel,
  isMobile = false,
}: GiftCertificateInfoProps) => {
  const isFullyPaid = priceWithDiscount === 0;
  return (
    <StyledGiftCertInfo isMobile={isMobile}>
      <StyledSuccessMarkIcon isMobile={isMobile} />
      <StyledSuccessText>
        <span>
          Вы активировали сертификат на{NBSP}
          {formatCurrency(discount)}.
        </span>
        {isMobile ? ' ' : <br />}
        {isFullyPaid ? (
          <span>Этого{NBSP}достаточно, чтобы забронировать выбранный тур</span>
        ) : (
          <span>
            Чтобы{NBSP}начать бронирование доплатите разницу{' '}
            {formatCurrency(priceWithDiscount)}
          </span>
        )}
      </StyledSuccessText>

      <StyledContent>
        {isFullyPaid ? (
          <StyledAgreeBlock />
        ) : (
          <StyledSubText>
            Сертификат частично погашает стоимость тура.
          </StyledSubText>
        )}
        <StyledButtons isMobile={isMobile} isFullyPaid={isFullyPaid}>
          {isFullyPaid && (
            <StyledSubmitButton isMobile={isMobile} onClick={onSubmit}>
              Забронировать
            </StyledSubmitButton>
          )}
          <StyledCancelButton isMobile={isMobile} onClick={onCancel}>
            Не использовать сертификат
          </StyledCancelButton>
        </StyledButtons>
      </StyledContent>
    </StyledGiftCertInfo>
  );
};

const StyledSuccessMarkIcon = styled(SuccessMarkDone)<{ isMobile: boolean }>`
  width: 48px;
  margin-bottom: ${({ isMobile }) => (isMobile ? 8 : 16)}px;

  ${DiscountSuccessIconAnimation}
`;

const StyledSuccessText = styled.div`
  ${(props) => props.theme.typography.headline3};
  text-align: center;

  ${DiscountSuccessTitleAnimation}
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${DiscountSuccessContentAnimation}
`;

const StyledSubText = styled.div`
  margin-top: 8px;
  ${(props) => props.theme.typography.text1};
  font-size: 14px;
  line-height: 16px;
  color: ${(props) => props.theme.colors.blackLight};
`;

const StyledAgreeBlock = styled(AgreeBlock)`
  margin-top: 20px;

  div::after {
    background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff);
  }
`;

const StyledButtons = styled.div<{ isMobile: boolean; isFullyPaid: boolean }>`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  ${({ isMobile }) => (isMobile ? 'flex-direction: column' : '')};
  margin-top: ${({ isMobile, isFullyPaid }) =>
    isMobile && !isFullyPaid ? 16 : 24}px;
`;

const StyledSubmitButton = styled(Button).attrs({
  primary: true,
})`
  width: ${({ isMobile }) => (isMobile ? '100%' : '230px')};
  height: ${({ isMobile }) => (isMobile ? 43 : 40)}px;
  font-size: 14px;
  ${({ isMobile }) =>
    isMobile ? 'margin-bottom: 14px' : 'margin-right: 16px'};
`;

const StyledCancelButton = styled(Button)`
  width: ${({ isMobile }) => (isMobile ? '100%' : '230px')};
  height: ${({ isMobile }) => (isMobile ? 43 : 40)}px;
  font-size: 14px;
  border-radius: 2px;
`;

const StyledGiftCertInfo = styled.div<{ isMobile: boolean }>`
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${({ isMobile }) =>
    isMobile &&
    css`
      ${StyledSubText} {
        font-size: 13px;
      }

      ${StyledSubmitButton} {
        width: 100%;
        height: 48px;
      }
    `}
`;

export default GiftCertificateInfo;
